// ASTRIXY.ONLINE — client-side cart (display only; server re-prices at checkout).
import { useCallback, useEffect, useState } from 'react';

const KEY = 'astrixy-online-cart';
const SESSION_KEY = 'astrixy-online-session';

export interface CartLine {
  listing_id: string;
  title: string;
  image: string | null;
  price: number;
  store_name: string | null;
  quantity: number;
  variant_id?: string | null;
  variant_name?: string | null;
}

export const cartLineKey = (line: Pick<CartLine, 'listing_id' | 'variant_id'>) =>
  `${line.listing_id}:${line.variant_id ?? 'default'}`;

function read(): CartLine[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((line): line is CartLine =>
      Boolean(line && typeof line.listing_id === 'string' && Number.isFinite(line.quantity)),
    );
  } catch {
    return [];
  }
}

function write(lines: CartLine[]) {
  window.localStorage.setItem(KEY, JSON.stringify(lines));
  window.dispatchEvent(new CustomEvent('astrixy-cart-change'));
}

export function getSessionId(): string {
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export function useMarketplaceCart() {
  const [lines, setLines] = useState<CartLine[]>([]);

  useEffect(() => {
    setLines(read());
    const sync = () => setLines(read());
    window.addEventListener('astrixy-cart-change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('astrixy-cart-change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const add = useCallback((line: CartLine) => {
    const next = read();
    const existing = next.find((l) => cartLineKey(l) === cartLineKey(line));
    if (existing) existing.quantity += line.quantity;
    else next.push(line);
    write(next);
  }, []);

  const setQuantity = useCallback((key: string, quantity: number) => {
    const next = read()
      .map((l) => (cartLineKey(l) === key ? { ...l, quantity: Math.max(1, quantity) } : l))
      .filter((l) => l.quantity > 0);
    write(next);
  }, []);

  const remove = useCallback((key: string) => {
    write(read().filter((l) => cartLineKey(l) !== key));
  }, []);

  const clear = useCallback(() => write([]), []);

  const itemCount = lines.reduce((sum, l) => sum + l.quantity, 0);
  const subtotal = lines.reduce((sum, l) => sum + l.price * l.quantity, 0);

  return { lines, add, setQuantity, remove, clear, itemCount, subtotal };
}
